// noinspection SpellCheckingInspection
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useOwnIndividual } from "marketplace/individuals/own-individual";
import { OWN_PERMISSIONS_PATH } from "permissions/_views/people/person-permissions-path";
import React from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import AccountSwitcher from "users/account-types/_views/account-switcher";
import { AccountType } from "users/account-types/account-type";

const MenuContainer = styled("div")(({ theme }) => ({
  minWidth: theme.spacing(40),
}));
const UserMenuPopover = styled(Popover)(() => ({
  "& .MuiPaper-root": {
    backdropFilter: "blur(10px) brightness(105%)",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
  },
}));
const UserAvatar = styled(Avatar)<{ accounttype: AccountType }>(({ theme, accounttype }) => ({
  backgroundColor: accounttype === AccountType.Client ? "#E19941" : "#404558",
  color: "#FFF",
  fontSize: "1em",
  height: theme.spacing(6),
  width: theme.spacing(6),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.66em",
    height: theme.spacing(4),
    width: theme.spacing(4),
  },
}));
const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2, 1, 6.5),
}));
const OptionsMenu = styled(MenuList)(({ theme }) => ({
  padding: theme.spacing(0, 0, 1, 0),
}));

export default function UserMenu(props: { readonly restrictedOnboarding?: boolean }) {
  const restrictedOnboarding = props.restrictedOnboarding ?? false;
  const [avatarMenuAnchor, setAvatarMenuAnchor] = React.useState<Element>();

  const navigate = useNavigate();
  const session = useSession();
  const { photo } = useOwnIndividual();

  // No navigate("/login") after this: `logout` already loads the login page as a new document, and
  // a router navigation in the old one only re-rendered its pages over a session with no user.
  function handleLogoutClick() {
    setAvatarMenuAnchor(undefined);
    void session.logout();
  }

  return (
    <React.Fragment>
      {/* Named like its siblings in the header ("open notifications menu", "open navigation drawer").
          Without a label this control's accessible name was whatever initials the avatar happened to
          render, so a screen reader announced "CA button" and nothing could address it by role. */}
      <IconButton
        aria-haspopup="true"
        aria-label="open account menu"
        onClick={(event) => setAvatarMenuAnchor(event.currentTarget)}
      >
        {/* The photo from the person's marketplace profile, and their initials until it arrives or
            when they have none. The button's label names it, so the image itself is decorative. */}
        <UserAvatar accounttype={session.accountType} alt="" src={photo}>
          {session.user?.initials}
        </UserAvatar>
      </IconButton>
      <UserMenuPopover
        anchorEl={avatarMenuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(avatarMenuAnchor)}
        onClose={() => setAvatarMenuAnchor(undefined)}
      >
        <MenuContainer>
          {!restrictedOnboarding && <><AccountSwitcher /><Divider /></>}
          <UserInfoContainer>
            <div>
              <Typography variant="h6">{session.user?.fullName}</Typography>
              <Typography variant="body2">{session.user?.companyName}</Typography>
            </div>
          </UserInfoContainer>
          <OptionsMenu>
            <MenuItem
              onClick={() => {
                setAvatarMenuAnchor(undefined);
                navigate("/help");
              }}
            >
              <ListItemIcon>
                <ContactSupportIcon />
              </ListItemIcon>
              <ListItemText primary="Help and tutorials" />
            </MenuItem>
            {!restrictedOnboarding && <MenuItem
              onClick={() => {
                setAvatarMenuAnchor(undefined);
                navigate("/marketplace/profiles");
              }}
            >
              <ListItemIcon>
                <AccountBoxIcon />
              </ListItemIcon>
              <ListItemText primary="My Profiles" />
            </MenuItem>}
            {/* Every member may read their own permissions; only the page says what they hold (#6168). */}
            {!restrictedOnboarding && <MenuItem
              onClick={() => {
                setAvatarMenuAnchor(undefined);
                navigate(OWN_PERMISSIONS_PATH);
              }}
            >
              <ListItemIcon>
                <LockPersonOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="My permissions" />
            </MenuItem>}
            {!restrictedOnboarding && <MenuItem
              onClick={() => {
                setAvatarMenuAnchor(undefined);
                navigate("/settings");
              }}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>}
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </OptionsMenu>
        </MenuContainer>
      </UserMenuPopover>
    </React.Fragment>
  );
}
